import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import './LenderProfiles.css';

const EMPTY_LENDER = {
  lender_name: '',
  bank_name: '',
  account_name: '',
  account_address: '',
  routing_number: '',
  account_number: '',
};

const FIELDS = [
  { key: 'lender_name', label: 'Lender Name', required: true },
  { key: 'bank_name', label: 'Bank Name' },
  { key: 'account_name', label: 'Account Name' },
  { key: 'account_address', label: 'Account Address', textarea: true },
  { key: 'routing_number', label: 'Routing Number' },
  { key: 'account_number', label: 'Account Number' },
];

export default function LenderProfiles() {
  const [lenders, setLenders] = useState([]);
  const [editingId, setEditingId] = useState(null); // null = not editing, 'new' = adding, number = editing
  const [form, setForm] = useState(EMPTY_LENDER);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLenders = useCallback(async () => {
    try {
      const res = await api.get('/lenders');
      setLenders(res.data.lenders || []);
    } catch (err) {
      console.error('Failed to load lenders:', err);
    }
  }, []);

  useEffect(() => {
    loadLenders();
  }, [loadLenders]);

  const startNew = () => {
    setForm(EMPTY_LENDER);
    setEditingId('new');
    setError('');
  };

  const startEdit = (lender) => {
    setForm({
      lender_name: lender.lender_name || '',
      bank_name: lender.bank_name || '',
      account_name: lender.account_name || '',
      account_address: lender.account_address || '',
      routing_number: lender.routing_number || '',
      account_number: lender.account_number || '',
    });
    setEditingId(lender.id);
    setError('');
  };

  const cancel = () => {
    setEditingId(null);
    setForm(EMPTY_LENDER);
    setError('');
  };

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (!form.lender_name.trim()) {
      setError('Lender Name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId === 'new') {
        await api.post('/lenders', form);
      } else {
        await api.put(`/lenders/${editingId}`, form);
      }
      await loadLenders();
      cancel();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save lender.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lender) => {
    if (!window.confirm(`Delete lender "${lender.lender_name}"? This will not affect already-filled payoff PDFs.`)) return;
    try {
      await api.delete(`/lenders/${lender.id}`);
      await loadLenders();
    } catch {
      setError('Failed to delete lender.');
    }
  };

  return (
    <div className="lenders-page">
      <div className="lenders-header">
        <h1 className="page-title">Lender Profiles</h1>
        {editingId === null && (
          <button className="btn-primary" onClick={startNew}>+ Add New Lender</button>
        )}
      </div>

      {error && <div className="error-box">{error}</div>}

      {editingId !== null && (
        <div className="lender-form-card">
          <h2 className="form-title">{editingId === 'new' ? 'Add New Lender' : 'Edit Lender'}</h2>
          <div className="lender-fields">
            {FIELDS.map(f => (
              <div key={f.key} className={`lender-field ${f.textarea ? 'wide' : ''}`}>
                <label>
                  {f.label}
                  {f.required && <span className="required-mark"> *</span>}
                </label>
                {f.textarea ? (
                  <textarea
                    rows={2}
                    value={form[f.key]}
                    onChange={e => updateField(f.key, e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    value={form[f.key]}
                    onChange={e => updateField(f.key, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="lender-form-actions">
            <button className="btn-primary" onClick={save} disabled={saving || !form.lender_name.trim()}>
              {saving ? 'Saving...' : 'Save Lender'}
            </button>
            <button className="btn-secondary" onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      {lenders.length === 0 && editingId === null ? (
        <div className="empty-state">
          <p>No lenders saved yet.</p>
          <span>Click "+ Add New Lender" to add one.</span>
        </div>
      ) : (
        <div className="lender-list">
          {lenders.map(l => (
            <div key={l.id} className="lender-card">
              <div className="lender-card-info">
                <h3 className="lender-card-name">{l.lender_name}</h3>
                <div className="lender-card-grid">
                  {l.bank_name && <div><span className="meta-label">Bank</span><span>{l.bank_name}</span></div>}
                  {l.account_name && <div><span className="meta-label">Account Name</span><span>{l.account_name}</span></div>}
                  {l.routing_number && <div><span className="meta-label">Routing</span><span>{l.routing_number}</span></div>}
                  {l.account_number && <div><span className="meta-label">Account #</span><span>{l.account_number}</span></div>}
                  {l.account_address && <div className="full-width"><span className="meta-label">Account Address</span><span>{l.account_address}</span></div>}
                </div>
              </div>
              <div className="lender-card-actions">
                <button className="btn-secondary" onClick={() => startEdit(l)}>Edit</button>
                <button className="btn-icon btn-icon-danger" onClick={() => remove(l)} title="Delete">×</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
